import { useContext, useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { PlayArrow, Settings, Stop } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  summarizer,
  decryptURL,
  encryptURL,
  CustomPagination,
} from 'utils/functions';
import { toastWarning } from 'utils/toast';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import { OrdensContext } from 'contexts/OrdensContext';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { useModal } from 'components/Modals';
import Container from 'components/Container';
import Dropdown from 'components/Dropdown';
import Header from 'components/Header';
import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import PauseModal from './Modals/PauseModal';
import styles from './styles';
import moment from 'moment';

const ListaProcessos = ({ arr = [], selected, onPress }) => {
  const navigate = useNavigate();
  const { getURLRelatorio } = useContext(RelatoriosContext);

  if (!arr?.length) {
    return (
      <Box textAlign="center">
        <Typography variant="caption" textAlign="center" flex={1}>
          Nenhum processo encontrado
        </Typography>
      </Box>
    );
  }
  const menus = [
    {
      label: 'Qualidade',
      icon: 'star',
      action: (processo) =>
        navigate(
          `/app/Documento/Qualidade/${encryptURL({ processo_id: processo?.id })}`
        ),
    },
    {
      label: 'Detalhes',
      icon: 'visibility',
      action: (processo) =>
        navigate(`/app/Documento/${processo?.documento_id}`),
    },
    {
      label: 'Imprimir Etiqueta',
      icon: 'print',
      action: (processo) =>
        getURLRelatorio({
          data: { codigo: 'PROD000001', item: processo?.documento_id },
        }),
    },
  ];

  const Actions = ({ processo }) =>
    menus?.map(({ action, icon }) => (
      <IconButton edge="end" size="small" onClick={() => action(processo)}>
        <Icon fontSize="small">{icon}</Icon>
      </IconButton>
    ));

  const Primary = ({ processo }) => (
    <Typography fontWeight="bold">{processo?.processo}</Typography>
  );

  const Secondary = ({ processo }) => {
    const lines = [
      { label: 'Nº do Documento', value: processo?.documento },
      { label: 'Operação', value: processo?.operacao },
      { label: 'Referência', value: processo?.referencia },
    ];
    return (
      <Box>
        <Typography variant="subtitle2">{processo?.produto}</Typography>
        {lines?.map(({ label, value }, i) => (
          <Box key={i} display="flex">
            <Typography variant="caption" flex={1}>
              {label}:
            </Typography>
            <Typography variant="caption" flex={1}>
              {value || '-'}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const Progress = ({ processo }) => {
    const unidade_medida = processo?.unidade_medida || '';
    const quantidade_produzir = processo?.quantidade_produzir || 0;
    const quantidade_produzida = processo?.quantidade_produzida || 0;
    const progress = Math.round(
      (100 * processo?.quantidade_produzida) / processo?.quantidade_produzir
    );

    return (
      <Box sx={styles?.progressInsideContainer}>
        <Box sx={styles?.progressContent}>
          <CircularProgress
            size={60}
            value={100}
            thickness={5}
            variant="determinate"
            sx={styles?.progressBackground}
          />
          <CircularProgress
            size={60}
            thickness={5}
            variant="determinate"
            sx={styles?.progress}
            value={progress < 100 ? progress : 100}
          />
          <Box sx={styles?.progressLabelContainer}>
            <Typography variant="caption" sx={styles?.progressLabel}>
              {progress}%
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" sx={styles?.progressLabel}>
          {quantidade_produzida}/{quantidade_produzir} {unidade_medida}
        </Typography>
      </Box>
    );
  };

  return (
    <List style={styles?.card}>
      {arr?.map((p, i) => {
        return (
          <ListItem key={p?.id} disablePadding divider={i + 1 < arr?.length}>
            <ListItemButton
              selected={selected === p?.id}
              onClick={() => onPress(p?.id)}
            >
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <Box sx={styles?.listContainer}>
                <ListItemText
                  primary={<Primary processo={p} />}
                  secondary={<Secondary processo={p} />}
                  sx={styles?.card}
                />
                <Progress processo={p} />
                <Actions processo={p} />
              </Box>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

const Apontamento = () => {
  const navigate = useNavigate();
  const { hash } = useParams();
  const { openModal, closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const {
    getLoading,
    postLoading,
    getProcessos,
    getApontamentos,
    postApontamento,
  } = useContext(OrdensContext);
  const [selected, setSelected] = useState(null);
  const [processos, setProcessos] = useState([]);
  const [apontamentos, setApontamentos] = useState([]);
  const {
    documento = '',
    centro_de_custo_id = null,
    id: documento_item_id = null,
  } = Boolean(hash) ? decryptURL(hash) : {};
  const defaultValues = { documento, centro_de_custo_id, documento_item_id };
  const { control, getValues } = useForm({ defaultValues });
  const loading = getLoading || postLoading;
  const processo = processos?.find((p) => p?.id === selected);
  const iniciado = apontamentos?.find(
    (f) => Boolean(f?.inicio) && !Boolean(f?.fim)
  );

  const getHoras = (apontamentos = []) => {
    let tempo_total = 0;
    apontamentos?.map((a) => {
      if (Boolean(a?.inicio) && Boolean(a?.fim)) {
        const dif = moment(a?.fim)?.diff(moment(a?.inicio));
        tempo_total += moment?.duration(dif)?.asMilliseconds();
      }
    });

    const duracao = moment?.duration(tempo_total);
    const dias = Math?.floor(duracao?.asDays());
    const horas = duracao?.hours();
    const minutos = duracao?.minutes();
    const segundos = duracao?.seconds();

    return `${dias}d: ${horas}h: ${minutos}m: ${segundos}s`;
  };

  const totais = [
    {
      value: getHoras(apontamentos),
      label: 'Horas apontadas',
    },
    {
      value: `${summarizer(apontamentos, 'quantidade') || 0} ${processo?.unidade_medida || ''}`,
      label: 'Total produzido',
    },
    {
      value: `${summarizer(apontamentos, 'perda') || 0} ${processo?.unidade_medida || ''}`,
      label: 'Total perdido',
    },
  ];

  useEffect(() => {
    loadProcessos();
  }, []);

  const loadProcessos = () => {
    const params = getValues();

    const cb = (res) =>
      setProcessos(() => {
        const not_exists = !res?.some((s) => s?.id === selected);
        if (res?.length > 0 && not_exists) {
          setSelected(res[0]?.id);
          loadApontamentos(res[0]?.id);
        } else {
          loadApontamentos(selected);
        }
        return res;
      });

    getProcessos({ params, cb });
  };

  const loadApontamentos = (processo_id) => {
    if (processo_id) {
      const params = { processo_id };

      getApontamentos({ params, cb: setApontamentos });
    } else {
      setApontamentos([]);
    }
  };

  const handleApontamento = () => {
    if (Boolean(iniciado)) {
      openModal(<PauseModal processo={processo} onSubmit={handleCreate} />);
    } else {
      handleCreate();
    }
  };

  const handleProcesso = (processo_id) => {
    setSelected(processo_id);
    loadApontamentos(processo_id);
  };

  const handleCreate = (values) => {
    const quantidade = Number(values?.quantidade || 0);
    if (Boolean(quantidade)) {
      const loteReposicao = Number(processo?.lote_reposicao || 0);
      const possuiLoteReposicao = loteReposicao > 0;

      if (possuiLoteReposicao && quantidade > loteReposicao) {
        return toastWarning(
          `A quantidade máxima permitida é ${processo?.lote_reposicao}`
        );
      }
    }

    if (
      processo?.quantidade_produzida + quantidade >
      processo?.quantidade_produzir
    ) {
      const dif =
        (processo?.quantidade_produzir || 0) -
        (processo?.quantidade_produzida || 0);
      const excedente = quantidade - dif;
      if (
        !window.confirm(
          `A quantidade produzida está excedendo a quantidade a produzir em ${excedente} ${processo?.unidade_medida || ''}. Deseja continuar?`
        )
      ) {
        return;
      }
    }

    if (
      Boolean(iniciado) &&
      user?.configuracoes?.Producao?.parada_obrigatoria &&
      !Boolean(quantidade) &&
      !values?.tipo_parada_id
    ) {
      return toastWarning('Tipo de parada obrigatório');
    }

    const data = {
      ...values,
      id: iniciado?.id,
      users_id: user?.id,
      executante_id: user?.id,
      documento_item_processo_id: processo?.id,
      inicio: iniciado?.inicio || moment().format('YYYY-MM-DD HH:mm:ss'),
      fim: Boolean(iniciado) ? moment().format('YYYY-MM-DD HH:mm:ss') : null,
    };

    const cb = () => {
      if (Boolean(values)) closeModal();
      if (values?.troco) {
        navigate(-1);
      } else {
        loadProcessos();
      }
    };

    postApontamento({ data, cb });
  };

  const TempoCell = ({ row }) => {
    const lines = [
      {
        label: 'Início',
        value:
          moment(row?.inicio).isValid() &&
          moment(row?.inicio).format('DD/MM/YYYY [às] HH:mm'),
      },
      {
        label: 'Fim',
        value:
          moment(row?.fim).isValid() &&
          moment(row?.fim).format('DD/MM/YYYY [às] HH:mm'),
      },
      {
        label: 'Total',
        value: Boolean(row?.inicio) && Boolean(row?.fim) && getHoras([row]),
      },
    ];

    return (
      <Box flex={1}>
        {lines?.map(
          ({ label, value }, i) =>
            Boolean(value) && (
              <Box key={i?.toString()} display="flex">
                <Typography flex={1} variant="caption" fontWeight="bold">
                  {label}:{' '}
                </Typography>
                <Typography flex={2} variant="caption">
                  {value}
                </Typography>
              </Box>
            )
        )}
      </Box>
    );
  };

  const QuantidadeCell = ({ row }) => {
    const lines = [
      {
        label: 'Produzida',
        value: `${row?.quantidade || 0} ${processo?.unidade_medida || ''}`,
      },
      {
        label: 'Perda',
        value: `${row?.perda || 0} ${processo?.unidade_medida || ''}`,
      },
    ];

    return (
      <Box flex={1}>
        {lines?.map(
          ({ label, value }, i) =>
            Boolean(value) && (
              <Box key={i?.toString()} display="flex">
                <Typography flex={1} variant="caption" fontWeight="bold">
                  {label}:{' '}
                </Typography>
                <Typography flex={2} variant="caption">
                  {value}
                </Typography>
              </Box>
            )
        )}
      </Box>
    );
  };

  return (
    <Container>
      <Header titulo="Apontamento de Horas" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} xl={4}>
          <Grid container spacing={2}>
            {!Boolean(hash) && (
              <Grid item xs={12}>
                <Card title="Filtros">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Input
                        name="documento"
                        control={control}
                        label="Nº do Documento"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Dropdown
                        control={control}
                        options={drops?.Ccusto}
                        label="Centro de Custo"
                        name="centro_de_custo_id"
                      />
                    </Grid>
                    <Grid item xs={12} textAlign="center">
                      <Button
                        loading={getLoading}
                        onClick={loadProcessos}
                        variant="contained"
                      >
                        Aplicar
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            )}
            <Grid item xs={12}>
              <Card title="Processos">
                <ListaProcessos
                  arr={processos}
                  selected={selected}
                  onPress={handleProcesso}
                />
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} xl={8} display="flex">
          <Card title="Apontamentos" style={styles?.card}>
            <Grid container spacing={2}>
              <Grid item xs={12} textAlign="center">
                <Button
                  loading={loading}
                  onClick={handleApontamento}
                  variant={Boolean(iniciado) ? 'outlined' : 'contained'}
                  startIcon={Boolean(iniciado) ? <Stop /> : <PlayArrow />}
                >
                  Apontamento
                </Button>
              </Grid>
              <Grid item xs={12}>
                <DataGrid
                  rows={apontamentos}
                  columns={[
                    {
                      field: 'autor',
                      headerName: 'Autor',
                      flex: 1,
                      minWidth: 300,
                      sortable: false,
                    },
                    {
                      field: 'executante',
                      headerName: 'Executante',
                      flex: 1,
                      minWidth: 300,
                      sortable: false,
                    },
                    {
                      field: 'tempo',
                      headerName: 'Tempo',
                      flex: 1,
                      minWidth: 300,
                      sortable: false,
                      renderCell: (props) => <TempoCell {...props} />,
                    },
                    {
                      field: 'quantidade',
                      headerName: 'Quantidade',
                      flex: 1,
                      minWidth: 300,
                      sortable: false,
                      renderCell: (props) => <QuantidadeCell {...props} />,
                    },
                  ]}
                  loading={loading}
                  pagination
                  autoHeight
                  pageSize={5}
                  density="compact"
                  disableColumnMenu
                  showCellRightBorder
                  showColumnRightBorder
                  localeText={localeText}
                  disableSelectionOnClick
                  getRowHeight={() => 'auto'}
                  components={{
                    LoadingOverlay: LinearProgress,
                    Pagination: CustomPagination,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                {totais?.map(({ label, value }) => (
                  <Box key={label} display="flex" flex={1}>
                    <Typography variant="body2" fontWeight="bold" flex={1}>
                      {label}:
                    </Typography>
                    <Typography variant="body2" flex={2}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <Fade in={getLoading}>
        <Backdrop open={getLoading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default Apontamento;
